import { h, Component } from 'preact';
import { Spinner, Icon } from '@cube/blocks';
import { getTrackingAttributesForAsset, trackingProvider, trackEvent, trackProps, INPUT_KEYS, TrackingProvider, DIMENSION, EVENT } from '@cube/track';
import Slider from '@synacorforks/react-slick';
import cx from 'classnames';
import { compose } from '@cube/utils';
import { provide } from 'preact-context-provider';
import wire from 'wiretie';
import style from './style';
import { configure } from '../../config';
import { fetchSlides } from '../../lib/utils';
import { getCardWidget, getCardType } from '../../lib/collection';
import dlv from 'dlv';
import linkref from 'linkref';

const RATIO_MAP = {
	'3:2': 'threeByTwo',
	'4:3': 'fourByThree',
	'16:9': 'sixteenByNine',
	'16:10': 'sixteenByTen'
};

export const Pending = ({ cardSizeStyle }) => (
	<div class={cx(style.dcc, cardSizeStyle)}>
		<Spinner class={style.spinner} />
	</div>
);

export const Rejected = () => (
	null
);

// Arrow renders the Previous and Next arrows
export const Arrow = (props, context) => {
	const { direction, onClick, goToOpposite, isInfinite } = props;

	// This should be null at the beginning and end so we can use this to fake an infinite carousel
	let clickEvent = onClick || !isInfinite && goToOpposite;

	return (
		<div class={cx(style.arrow, style[direction])} onClick={clickEvent}>
			<Icon
				name={direction === 'left' ? 'back-cube' : 'go-cube'}
				{...trackProps({
					[DIMENSION.componentpath]: (direction === 'left' ? 'Previous' : 'Next') +  'Arrow'
				}, context)}
			/>
		</div>
	);
};

/**
 * The Dcc component is a carousel for Card components.
 */
@trackingProvider({
	[DIMENSION.componentpath]: 'Dcc'
})
export class Dcc extends Component {
	currentSlideIndex = 0;
	firedImpressions = [];

	getLink = getCardWidget('default').getLink;

	// customPaging displays (current / total) slides
	customPaging = (i) => (
		(this.props.variant=== 'P25-DCC-A') ?	<span> {' '} </span>:<span>{i + 1} / {this.props.slides.length}</span>
	);

	slickInit = () => {
		this.fireImpressionTracking(0);
	};

	goToBeginning = () => {
		if (dlv(this.refs, 'slider.slickGoTo')) {
			this.refs.slider.slickGoTo(0, false);
		}
	};

	goToEnd = () => {
		if (dlv(this.refs, 'slider.slickGoTo') && this.props.slides.length) {
			this.refs.slider.slickGoTo(this.props.slides.length - 1, false);
		}
	};

	afterChange = (i) => {
		// Clear our rotate handler if it set as we have already moved through some mechanism other than it
		if (this.rotateTimeoutHandle) {
			window.clearTimeout(this.rotateTimeoutHandle);
			this.rotateTimeoutHandle = null;
		}

		let autoRotate = dlv(this.props, 'carouselSettings.autoRotate', true),
			autoRotateFrequency = dlv(this.props, 'carouselSettings.autoRotateFrequency');

		// Fake an infinite carousel so we don't get cloned slides
		if (i === this.props.slides.length - 1 && autoRotate && autoRotateFrequency ) {
			this.rotateTimeoutHandle = window.setTimeout(() => {
				this.refs.slider.slickGoTo(0, false);
				this.rotateTimeoutHandle = null;
			}, autoRotateFrequency);
		}

		// If just changed from first slide and autoRotateFrequency is provided, then use it.
		if (i === 1 && autoRotateFrequency) {
			this.setState({ autoplaySpeed: autoRotateFrequency });
		}

		this.currentSlideIndex = i;
		this.fireImpressionTracking(i);

		//update slides JSX to prepare the upcoming slides so they fetch their images
		this.setState({});
	};

	fireImpressionTracking = (i) => {
		if (this.props.slides.length >= i && this.firedImpressions.indexOf(i) === -1) {
			trackEvent(this.context, {
				[INPUT_KEYS.action]: EVENT.impression,
				...getTrackingAttributesForAsset(this.props.slides[i]),
				[DIMENSION.componentpath]: 'Card',
				[DIMENSION.clickindex]: i
			});

			this.firedImpressions.push(i);
		}
	};

	// mapCarouselToSlick maps our own terminology to slick specific settings
	mapCarouselToSlick(autoplaySpeed) {
		const slickSettings = {};
		const carouselSettings = this.props.carouselSettings || {};
		const mapping = {
			autoRotate: 'autoplay',
			showPageCount: 'dots',
			transitionSpeed: 'speed'
		};

		Object.keys(carouselSettings).forEach((key, i) => {
			if (mapping[key]) {
				slickSettings[mapping[key]] = carouselSettings[key];
			}
		});

		if (autoplaySpeed) {
			slickSettings.autoplaySpeed = autoplaySpeed;
		}

		slickSettings.infinite = carouselSettings.infinite || false;

		if (carouselSettings.showArrows) {
			slickSettings.nextArrow = <Arrow direction="right" goToOpposite={this.goToBeginning} isInfinite={slickSettings.infinite} />;
			slickSettings.prevArrow = <Arrow direction="left" goToOpposite={this.goToEnd} isInfinite={slickSettings.infinite} />;
		}
		else {
			slickSettings.arrows = false;
		}

		if (carouselSettings.showPageCount) {
			slickSettings.customPaging = this.customPaging;
		}

		slickSettings.afterChange = this.afterChange;
		slickSettings.onInit = this.slickInit;

		// Set clickable true to work around bug https://github.com/akiran/react-slick/issues/1298
		slickSettings.onSwipe = () => {
			if (dlv(this, 'refs.slider.innerSlider')) {
				this.refs.slider.innerSlider.clickable = true;
			}
		};

		return slickSettings;
	}

	/**
	 * Primarily used to send tracking information about the card that was clicked, but
	 * also allows child components to pause the carousel from advancing in the case that
	 * this card is playing a video.
	 *
	 * @param {Object} e - Event object
	 * @param {Boolean} e.pause - Whether or not to stop the carousel from advancing to new slides
	 */
	onCardClick = ({ pause = false }) => {
		if (pause) this.refs.slider.slickPause();
	};

	onEnter = (e) => {
		if (e.keyCode === 13 && this.currentSlideIndex) {
			let currentSlide = dlv(this.refs.slider, `innerSlider.list.childNodes.0.childNodes.${this.currentSlideIndex+1}`);
			if (currentSlide) {
				// Get the top level element of the current slide and fire a click action on it
				currentSlide.firstElementChild.firstElementChild.click();
			}
		}
	};

	getCardSizeStyle(cardSize) {
		if (!cardSize) return '';

		let suffix = (cardSize.indexOf('-') > 0 ? cardSize.substring(cardSize.indexOf('-') + 1) : '3:2');
		if (RATIO_MAP[suffix]) {
			return style[RATIO_MAP[suffix]];
		}
		return '';
	}

	componentWillMount() {
		// On mount, use first slide speed if available.  If not then use default from config
		const carouselSettings = this.props.carouselSettings || {};
		this.setState({
			autoplaySpeed: (carouselSettings.firstSlideAutoRotateFrequency ? carouselSettings.firstSlideAutoRotateFrequency : carouselSettings.autoRotateFrequency)
		});
	}

	componentDidUpdate() {
		let list = dlv(this.refs, 'slider.innerSlider.list');
		if (list && list.getAttribute('tabindex') === null) {
			// This allows user to tab to the div where they can then use the arrow keys to navigate.
			list.setAttribute('tabindex', 0);

			// This sets the priority for the screen readers so it will read as each slide goes by.
			list.setAttribute('aria-live', 'polite');

			// This handles the enter key acting like a click action to follow the card.  This will only target a
			// main click action for the card and will not work for multi-content cards like the truck card.
			list.addEventListener('keypress', this.onEnter);
		}
	}

	renderSlides({ slides, cardSize, lazySlideWindowSize, overlayMode, variant }) {
		let cardTypeCounts = {};
		return slides.map( (slide, i) => {
			let cardType = getCardType(slide),
				Card = getCardWidget(slide).Card,
				lazy = Math.abs(i-this.currentSlideIndex) > lazySlideWindowSize;

			if (cardTypeCounts[cardType]) cardTypeCounts[cardType]++;
			else cardTypeCounts[cardType] = 1;

			return (
				<TrackingProvider {...{ [DIMENSION.clickindex]: i+1, [DIMENSION.variant]: variant }}>
					<Card
						settings={slide} //<-to deprecate
						scsData={slide}
						cardSize={cardSize}
						lazy={lazy}
						onCardClick={this.onCardClick}
						startContentServiceOrigin={this.props.startContentServiceOrigin}
						streamClient={this.props.streamClient}
						overlayMode={overlayMode}
						slideNumber={i+1}
						cardTypeCount={cardTypeCounts[cardType]}
						variant={variant}
					/>
				</TrackingProvider>
			);
		});
	}

	render({ cardSize, lazySlideWindowSize, slides = [], pending, rejected, overlayMode, variant },  { autoplaySpeed }) {
		let cardSizeStyle = this.getCardSizeStyle(cardSize);

		if (pending && !slides.length) {
			return <Pending cardSizeStyle={cardSizeStyle} />;
		}

		if (rejected || !slides.length) {
			return <Rejected />;
		}

		//TODO: the this.mapCarouselToSlick call rebuilds the UI on every render. This should only be called when needed.
		return (
			<div data-tb-region="dcc_desktop_v2">
				<Slider
					ref={linkref(this, 'slider')}
					{...this.mapCarouselToSlick(autoplaySpeed)}
					class={cx(this.props.variant === 'P25-DCC-A' ? style.dccP25 : style.dcc, style[overlayMode], style[variant], cardSizeStyle)}
				>
					{this.renderSlides({ cardSize, slides, lazySlideWindowSize, overlayMode, variant })}
				</Slider>
			</div>
		);
	}
}

/**
 * This component wraps the Dcc and adds the default configuration
 * as well populates itself using a function provided
 * via context `dcc:{ fetchSlides }`.
 *
 * fetchSlides is implemented as `fetchSlides(feed, site, apiOrigin, maxSlideCount)`
 * and returns a Promise that resolves to and array of SCS card data objects.
 */
export const SlideFetchingDcc = compose(
	configure({
		startContentServiceOrigin: 'startContentServiceOrigin',
		locationServiceOrigin: 'locationServiceOrigin',
		streamClient: 'streamClient',
		restrictions: 'restrictions',
		layout: 'dccV2Config.layout',
		carouselSettings: 'dccV2Config.carouselSettings',
		maxSlideCount: 'dccV2Config.maxSlideCount',
		lazySlideWindowSize: 'dccV2Config.lazySlideWindowSize'
	}),
	wire('dcc', ({ startContentServiceOrigin, layout, streamClient, maxSlideCount, restrictions, locationServiceOrigin }) => ({
		slides: ['fetchSlides', layout, streamClient, startContentServiceOrigin, maxSlideCount, { restrictions, locationServiceOrigin } ]
	}))
)(Dcc);

/**
 * The Dcc component with default configuration and using the standard `fetchSlides()`
 * as implemented for the SCS.
 */
export default compose(
	provide({ dcc: { fetchSlides } })
)(SlideFetchingDcc);
